import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslate } from "../context/TranslationContext";
import {
  useCreateGuidelineMutation,
  useGetGuidelineQuery,
  useUpdateGuidelineMutation,
} from "../redux/Apis/Guideline";
import { isVideoMedia } from "../utils/helper";
import usePresignedUpload from "./usePresignedUpload";

const getGifDurationInSeconds = async (file) => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (bytes.length < 13) {
    throw new Error("Invalid GIF file");
  }

  const signature = String.fromCharCode(...bytes.slice(0, 6));
  if (signature !== "GIF87a" && signature !== "GIF89a") {
    throw new Error("Unsupported GIF format");
  }

  let offset = 6;

  // Logical Screen Descriptor
  offset += 7;
  const packed = bytes[10];
  const hasGlobalColorTable = (packed & 0x80) !== 0;
  if (hasGlobalColorTable) {
    const globalColorTableSize = 3 * 2 ** ((packed & 0x07) + 1);
    offset += globalColorTableSize;
  }

  let durationMs = 0;
  let currentDelayMs = 0;

  while (offset < bytes.length) {
    const blockId = bytes[offset];

    if (blockId === 0x3b) {
      break;
    }

    if (blockId === 0x21) {
      const label = bytes[offset + 1];

      if (label === 0xf9) {
        const blockSize = bytes[offset + 2];
        if (blockSize !== 4 || offset + 7 >= bytes.length) {
          throw new Error("Invalid GIF graphic control extension");
        }

        const delay = bytes[offset + 4] | (bytes[offset + 5] << 8);
        currentDelayMs = delay * 10;
        offset += 8;
        continue;
      }

      offset += 2;
      while (offset < bytes.length) {
        const subBlockSize = bytes[offset];
        offset += 1;
        if (subBlockSize === 0) {
          break;
        }
        offset += subBlockSize;
      }
      continue;
    }

    if (blockId === 0x2c) {
      if (offset + 9 >= bytes.length) {
        throw new Error("Invalid GIF image descriptor");
      }

      const imagePacked = bytes[offset + 9];
      offset += 10;

      const hasLocalColorTable = (imagePacked & 0x80) !== 0;
      if (hasLocalColorTable) {
        const localColorTableSize = 3 * 2 ** ((imagePacked & 0x07) + 1);
        offset += localColorTableSize;
      }

      offset += 1; // LZW minimum code size

      while (offset < bytes.length) {
        const subBlockSize = bytes[offset];
        offset += 1;
        if (subBlockSize === 0) {
          break;
        }
        offset += subBlockSize;
      }

      durationMs += currentDelayMs;
      currentDelayMs = 0;
      continue;
    }

    throw new Error("Unsupported GIF block structure");
  }

  return durationMs / 1000;
};

const getVideoDurationInSeconds = (file) =>
  new Promise((resolve, reject) => {
    const videoUrl = URL.createObjectURL(file);
    const videoEl = document.createElement("video");

    videoEl.preload = "metadata";
    videoEl.src = videoUrl;

    videoEl.onloadedmetadata = () => {
      URL.revokeObjectURL(videoUrl);
      resolve(videoEl.duration);
    };

    videoEl.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      reject(new Error("Invalid video file"));
    };
  });

const usePostGuidelineController = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("community");
  const { user } = useSelector((state) => state.authSlice);
  const [content, setContent] = useState("");
  const [imgorgif, setImgorgif] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const toastShownRef = useRef(false);
  const { data: guidelineData, isSuccess: isFetchSuccess } =
    useGetGuidelineQuery({ id, params: { lang } }, { skip: !id });
  const [createGuideline, { isSuccess: isPostSuccess, isLoading: isPosting }] =
    useCreateGuidelineMutation();

  const [
    updateGuideline,
    { isSuccess: isUpdateSuccess, isLoading: isUpdating },
  ] = useUpdateGuidelineMutation();

  const { uploadFileWithPresignedUrl } = usePresignedUpload();

  useEffect(() => {
    if (id && isFetchSuccess && guidelineData?.data?.content) {
      setContent(guidelineData?.data?.content);
      setImgorgif(guidelineData?.data?.imgorgif || "");
    }
  }, [id, isFetchSuccess, guidelineData]);

  useEffect(() => {
    if ((isPostSuccess || isUpdateSuccess) && !toastShownRef.current) {
      toastShownRef.current = true;

      toast.success(id ? t("toastPostUpdated") : t("toastPostCreated"));

      setContent("");
      setImgorgif("");
      setThumbnailFile(null);
      setPreviewUrl(null);
      setIsRefetching(true);

      if (id) {
        navigate("/community");
      }
    }
  }, [isPostSuccess, isUpdateSuccess, navigate, id, t]);

  const onChangeContent = useCallback((e) => {
    setContent(e?.target?.value);
  }, []);

  const onChangeImgorgif = useCallback(
    (e) => {
      const file = e.target.files?.[0];

      if (!file) return;

      // Check GIF duration (max 7 sec)
      if (file.type === "image/gif") {
        const gifUrl = URL.createObjectURL(file);

        getGifDurationInSeconds(file)
          .then((durationInSeconds) => {
            if (durationInSeconds > 7) {
              const htmlMessage = t("gifMax7Sec")
                .replace(
                  "{{duration}}",
                  `<b>${durationInSeconds.toFixed(1)}</b>`,
                )
                .replace("{{max}}", "<b>7</b>");

              toast.error(
                <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />,
              );

              setThumbnailFile(null);
              setPreviewUrl(null);
              e.target.value = "";

              URL.revokeObjectURL(gifUrl);

              return;
            }

            setThumbnailFile(file);
            setPreviewUrl(gifUrl);
          })
          .catch((err) => {
            console.error("GIF validation failed:", err);

            toast.error(t("gifValidationFailed") || "Failed to validate GIF");

            e.target.value = "";

            URL.revokeObjectURL(gifUrl);
          });

        return;
      }

      // Check video duration (max 5 sec)
      if (file.type.startsWith("video/")) {
        const videoUrl = URL.createObjectURL(file);

        getVideoDurationInSeconds(file)
          .then((durationInSeconds) => {
            if (durationInSeconds > 5) {
              const htmlMessage = (
                t("videoMax5Sec") ||
                "Video duration is {{duration}} sec, max allowed is {{max}} sec."
              )
                .replace(
                  "{{duration}}",
                  `<b>${durationInSeconds.toFixed(1)}</b>`,
                )
                .replace("{{max}}", "<b>5</b>");

              toast.error(
                <div dangerouslySetInnerHTML={{ __html: htmlMessage }} />,
              );

              setThumbnailFile(null);
              setPreviewUrl(null);
              e.target.value = "";

              URL.revokeObjectURL(videoUrl);

              return;
            }

            setThumbnailFile(file);
            setPreviewUrl(videoUrl);
          })
          .catch((err) => {
            console.error("Video validation failed:", err);

            toast.error(
              t("videoValidationFailed") || "Failed to validate video",
            );

            e.target.value = "";

            URL.revokeObjectURL(videoUrl);
          });

        return;
      }

      // Normal image upload
      setThumbnailFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [t],
  );

  const onRemoveSelectedFile = useCallback(() => {
    setThumbnailFile(null);
    setPreviewUrl(null);

    const fileInputs = document.querySelectorAll("input[type='file']");
    fileInputs.forEach((input) => {
      input.value = "";
    });
  }, []);

  const onClickPost = useCallback(async () => {
    if (!user) {
      toast.info(t("loginToPost"));
      return;
    }

    if (!content.trim()) {
      toast.error(t("toastEnterContent"));
      return;
    }

    if (!thumbnailFile && !imgorgif) {
      toast.error(t("toastSelectMedia"));
      return;
    }

    let mediaKey = imgorgif;

    if (thumbnailFile instanceof File) {
      setIsUploading(true);
      const key = await uploadFileWithPresignedUrl(thumbnailFile);
      setIsUploading(false);
      if (!key) return;
      mediaKey = key;
    }

    try {
      const payload = { content };
      if (mediaKey) payload.imgorgif = mediaKey;

      if (id) {
        await updateGuideline({ id, body: payload, params: { lang } }).unwrap();
      } else {
        await createGuideline({ payload, params: { lang } }).unwrap();
      }
    } catch (error) {
      console.error("Post/Update failed:", error);
      toast.error(error?.data?.message || t("toastErrorGeneric"));
    }
  }, [
    user,
    content,
    thumbnailFile,
    imgorgif,
    id,
    createGuideline,
    updateGuideline,
    uploadFileWithPresignedUrl,
    t,
  ]);

  return {
    values: {
      isLoading: isUploading || isPosting || isUpdating,
      isRefetching,
      content,
      imgorgif,
      previewUrl,
      isVideoPreview: thumbnailFile
        ? thumbnailFile.type.startsWith("video/")
        : isVideoMedia(imgorgif),
      isEditing: !!id,
      isPostSuccess,
      isUpdateSuccess,
    },
    functions: {
      onClickPost,
      onChangeContent,
      onChangeImgorgif,
      onRemoveSelectedFile,
    },
  };
};

export default usePostGuidelineController;
